import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useAuth } from '../../context/auth';
import { useMessage } from '../../context/message';
import { useRackPricing } from '../../context/rackPricing';

const ALL_CITIES = '__ALL_CITIES__';
const PADD_OPTION_PREFIX = '__PADD_';
const PADD_OPTIONS = ['1A', '1B', '1C', '2', '3', '4', '5'];
const PADD_STATES = {
  '1A': ['CT', 'ME', 'MA', 'NH', 'RI', 'VT'],
  '1B': ['DE', 'DC', 'MD', 'NJ', 'NY', 'PA'],
  '1C': ['FL', 'GA', 'NC', 'SC', 'VA', 'WV'],
  2: ['IL', 'IN', 'IA', 'KS', 'KY', 'MI', 'MN', 'MO', 'NE', 'ND', 'OH', 'OK', 'SD', 'TN', 'WI'],
  3: ['AL', 'AR', 'LA', 'MS', 'NM', 'TX'],
  4: ['CO', 'ID', 'MT', 'UT', 'WY'],
  5: ['AK', 'AZ', 'CA', 'HI', 'NV', 'OR', 'WA'],
};

export default function MillsPricingAccessDialog({ open, setOpen, user }) {
  const { auth } = useAuth();
  const { setMessage } = useMessage();
  const { refreshRackPricingAccess } = useRackPricing();
  const [availableCities, setAvailableCities] = useState([]);
  const [selectedCityCodes, setSelectedCityCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !user) return;

    setLoading(true);
    fetch(`/api/mills-price/access/${user.user_id}`, {
      headers: { 'x-access-token': sessionStorage.getItem('token') },
    })
      .then((response) => auth(response))
      .then((response) => {
        if (response.message?.type === 'error') throw new Error(response.message.text);
        const nextAvailableCities = response.availableCities || [];
        const nextCityCodes = response.cityCodes || [];
        const hasAllCities = nextAvailableCities.length > 0
          && nextAvailableCities.every((city) => nextCityCodes.includes(city.code));
        const selectedPadds = PADD_OPTIONS
          .filter((paddNumber) => {
            const states = PADD_STATES[paddNumber];
            const paddCities = nextAvailableCities.filter((city) => states.includes(city.state));
            return paddCities.length > 0
              && paddCities.every((city) => nextCityCodes.includes(city.code));
          })
          .map((paddNumber) => `${PADD_OPTION_PREFIX}${paddNumber}`);

        setAvailableCities(nextAvailableCities);
        setSelectedCityCodes(hasAllCities ? [ALL_CITIES] : [...selectedPadds, ...nextCityCodes]);
      })
      .catch((error) => {
        setMessage({ type: 'error', text: error.message || 'Unable to load Mills pricing access.' });
      })
      .finally(() => setLoading(false));
  }, [auth, open, setMessage, user]);

  const handleSave = () => {
    const cityCodes = selectedCityCodes.includes(ALL_CITIES)
      ? availableCities.map((city) => city.code)
      : selectedCityCodes.filter((cityCode) => !cityCode.startsWith(PADD_OPTION_PREFIX));

    setSaving(true);
    fetch(`/api/mills-price/access/${user.user_id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'x-access-token': sessionStorage.getItem('token'),
      },
      body: JSON.stringify({ cityCodes }),
    })
      .then((response) => auth(response))
      .then((response) => {
        setMessage(response.message);
        if (response.message?.type !== 'success') return null;

        return refreshRackPricingAccess(user.user_id, (response.cityCodes || []).length > 0)
          .catch((error) => {
            setMessage({
              type: 'error',
              text: `Access was saved, but Mills data could not be reloaded: ${error.message}`,
            });
          })
          .finally(() => setOpen(false));
      })
      .catch((error) => {
        setMessage({ type: 'error', text: error.message || 'Unable to save Mills pricing access.' });
      })
      .finally(() => setSaving(false));
  };

  const citiesByCode = useMemo(() => availableCities.reduce((cities, city) => ({
    ...cities,
    [city.code]: city,
  }), {}), [availableCities]);

  const cityLabel = (cityCode) => {
    if (cityCode === ALL_CITIES) return 'All cities';
    if (cityCode.startsWith(PADD_OPTION_PREFIX)) {
      return `PADD ${cityCode.replace(PADD_OPTION_PREFIX, '')}`;
    }
    const city = citiesByCode[cityCode];
    if (!city) return cityCode;
    return `${city.city}${city.state ? `, ${city.state}` : ''} (${city.code})`;
  };

  const paddCityCodes = (paddNumber) => availableCities
    .filter((city) => PADD_STATES[paddNumber].includes(city.state))
    .map((city) => city.code);

  return (
    <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Mills Rack Price Access</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Select the rack cities available to {user?.username || 'this user'}. Free Mills scorecard symbols remain available automatically.
        </Typography>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
            <CircularProgress size={28} />
          </div>
        ) : (
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={[
              ALL_CITIES,
              ...PADD_OPTIONS.map((paddNumber) => `${PADD_OPTION_PREFIX}${paddNumber}`),
              ...availableCities.map((city) => city.code),
            ]}
            value={selectedCityCodes}
            onChange={(event, cityCodes) => {
              if (!cityCodes.includes(ALL_CITIES)) {
                const previousPadds = selectedCityCodes.filter((cityCode) => (
                  cityCode.startsWith(PADD_OPTION_PREFIX)
                ));
                const nextPadds = cityCodes.filter((cityCode) => (
                  cityCode.startsWith(PADD_OPTION_PREFIX)
                ));
                const addedPadds = nextPadds.filter((paddOption) => !previousPadds.includes(paddOption));
                const removedPadds = previousPadds.filter((paddOption) => !nextPadds.includes(paddOption));
                const nextCodes = new Set(cityCodes.filter((cityCode) => (
                  !cityCode.startsWith(PADD_OPTION_PREFIX)
                )));

                addedPadds.forEach((paddOption) => {
                  const paddNumber = paddOption.replace(PADD_OPTION_PREFIX, '');
                  paddCityCodes(paddNumber).forEach((cityCode) => nextCodes.add(cityCode));
                });
                removedPadds.forEach((paddOption) => {
                  const paddNumber = paddOption.replace(PADD_OPTION_PREFIX, '');
                  paddCityCodes(paddNumber).forEach((cityCode) => nextCodes.delete(cityCode));
                });

                const orderedCodes = availableCities
                  .map((city) => city.code)
                  .filter((cityCode) => nextCodes.has(cityCode));
                const completePadds = nextPadds.filter((paddOption) => {
                  const paddNumber = paddOption.replace(PADD_OPTION_PREFIX, '');
                  return paddCityCodes(paddNumber).every((cityCode) => nextCodes.has(cityCode));
                });

                setSelectedCityCodes(
                  orderedCodes.length === availableCities.length
                    ? [ALL_CITIES]
                    : [...completePadds, ...orderedCodes]
                );
                return;
              }

              if (selectedCityCodes.includes(ALL_CITIES)) {
                const remainingOptions = cityCodes.filter((cityCode) => cityCode !== ALL_CITIES);
                const paddOptions = remainingOptions.filter((cityCode) => (
                  cityCode.startsWith(PADD_OPTION_PREFIX)
                ));
                const nextCodes = new Set(remainingOptions.filter((cityCode) => (
                  !cityCode.startsWith(PADD_OPTION_PREFIX)
                )));
                paddOptions.forEach((paddOption) => {
                  const paddNumber = paddOption.replace(PADD_OPTION_PREFIX, '');
                  paddCityCodes(paddNumber).forEach((cityCode) => nextCodes.add(cityCode));
                });
                const orderedCodes = availableCities
                  .map((city) => city.code)
                  .filter((cityCode) => nextCodes.has(cityCode));
                setSelectedCityCodes([...paddOptions, ...orderedCodes]);
                return;
              }

              setSelectedCityCodes(
                [ALL_CITIES]
              );
            }}
            getOptionLabel={cityLabel}
            renderTags={(cityCodes, getTagProps) => cityCodes
              .map((cityCode, index) => ({ cityCode, index }))
              .filter(({ cityCode }) => !cityCode.startsWith(PADD_OPTION_PREFIX))
              .map(({ cityCode, index }) => (
                <Chip
                  key={cityCode}
                  label={cityLabel(cityCode)}
                  color="primary"
                  size="small"
                  {...getTagProps({ index })}
                />
              ))}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Allowed rack cities"
                variant="outlined"
                margin="normal"
                placeholder={selectedCityCodes.length ? '' : 'Choose one or more cities'}
              />
            )}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          disabled={loading || saving}
        >
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
